import './JtsForm.scss';

import React                               from 'react';
import get                                 from 'lodash/get';
import notification                        from 'antd/lib/notification';
import Form                                from 'antd/lib/form';
import Button                              from 'antd/lib/button';
import { WrappedFormUtils }                from 'antd/lib/form/Form';
import { JtsFormMessage, MessageType }     from './JtsFormMessage';
import { deleteEmptyPropertiesFromObject } from '../utils/deleteEmptyPropertiesFromObject';

export interface IFormState {
	errors: string[];
	loading?: boolean;
}

export interface IFormProps extends WrappedFormUtils {
	getFieldInstance: (name: string) => any;
}

export interface IJtsFormProps {
	cancelText?: string | JSX.Element;
	footerComponent?: JSX.Element;
	form: IFormProps;
	headerComponent?: JSX.Element;
	mapParams?: (values) => void;
	notificationErrorMessage?: string | JSX.Element;
	notificationSuccessMessage?: string | JSX.Element;
	onCancel?: () => void;
	onSubmit?: (params) => Promise<any>;
	onSuccess?: (res) => void;
	submitText?: string | JSX.Element;
}

export class JtsForm extends React.Component<IJtsFormProps, IFormState> {

	public static defaultProps = {
		cancelText: 'Annuler',
		submitText: 'Enregistrer',
	};

	public state = {
		errors: [],
		loading: false,
	};

	public componentDidMount() {
		this.selectFirstField();
	}

	public render() {
		const {
			children,
			footerComponent,
			headerComponent,
		} = this.props;

		const { errors } = this.state;

		return <Form
			className="jts-form-container"
			onSubmit={(e) => this.onSubmit(e)}
		>
			<div className="jts-form-body-wrapper">
				{errors.length ? <JtsFormMessage message={errors} type={MessageType.Error}/> : null}

				{headerComponent ? headerComponent : null}

				{children}
			</div>

			{footerComponent ? footerComponent : this.renderButtons()}
		</Form>;
	}

	private onSubmit(e): Promise<any> {
		const { loading } = this.state;

		const {
			form,
			mapParams,
			notificationErrorMessage,
			notificationSuccessMessage,
			onSubmit,
			onSuccess,
		} = this.props;

		e.preventDefault();

		if (loading || !onSubmit) {
			return Promise.reject();
		}

		this.setState({ loading: true });

		return new Promise((resolve, reject) => {
			form.validateFields((err, values) => {
				if (err) {
					return reject(err);
				}

				const params = mapParams ? mapParams(values) : values;

				if (params.translations) {
					deleteEmptyPropertiesFromObject(params.translations);
				}

				return onSubmit(params).then(
					result => {
						if (notificationSuccessMessage) {
							notification.success({
								description: '',
								message: notificationSuccessMessage,
								className: 'app-notification success'
							});
						}

						resolve(result);

						return result;
					},
					(error: any) => {
						const hydraDescription = get(error, 'connectorError.response.data.hydra:description', false);
						const errors = [];
						const violations = get(error, 'connectorError.response.data.violations', []);

						if (violations.length) {
							const fields = {};
							for (const fieldError of violations) {
								if (!fields[fieldError.propertyPath]) {
									fields[fieldError.propertyPath] = {
										value: values[fieldError.propertyPath],
										errors: []
									};

									errors.push(fieldError.message as never);
								}

								fields[fieldError.propertyPath].errors.push(new Error(fieldError.message));
							}

							form.setFields(fields);
						} else if (hydraDescription) {
							errors.push(hydraDescription as never);
						}

						if (notificationErrorMessage) {
							notification.error({
								description: '',
								message: notificationErrorMessage,
								className: 'app-notification error'
							});
						}

						this.setState({errors});

						reject(error);
					}
				);
			});
		}).then((res) => {
			this.setState({loading: false});

			if (onSuccess) {
				onSuccess(res);
			}
		}).catch(() => {
			this.setState({loading: false});
		});
	}

	private renderButtons(): JSX.Element {
		const {
			cancelText,
			form,
			onCancel,
			submitText,
		} = this.props;

		const { loading } = this.state;

		return <Form.Item className="jts-form-footer-actions">
			<div className="divider"/>
			<div className="form-footer-buttons">
				<Button
					onClick={() => onCancel && onCancel()}
					loading={loading}
				>
					{cancelText}
				</Button>

				<Button
					htmlType="submit"
					loading={loading}
					type="primary"
					onClick={(e) => this.onSubmit(e)}
				>
					{submitText}
				</Button>
			</div>
		</Form.Item>;
	}

	/**
	 * Allow to focus first field on load
	 */
	private selectFirstField(): void {
		const { form } = this.props;

		const firstPropertyName = Object.keys(this.props.form.getFieldsValue())[0];
		const instance = form.getFieldInstance(firstPropertyName);

		if (instance && instance.focus) {
			instance.focus();
		}
	}
}
